import { useState, useCallback, useMemo, useEffect } from 'react'
import { AlertCircle, CalendarDays } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { TimesheetFilters } from './TimesheetFilters'
import { TimesheetGrid } from './TimesheetGrid'
import { TimesheetDayDetailsModal } from './TimesheetDayDetailsModal'
import { useTimesheetReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { exportTimesheetToExcel } from '../../utils/exportTimesheetToExcel'
import type { TimesheetReportFilters, TimesheetEmployee, TimesheetDay } from '../../types/reports.types'

interface SelectedDay {
  employee: TimesheetEmployee
  dateStr: string
  day: TimesheetDay
}

export function TimesheetReport() {
  const today = new Date()
  const [filters, setFilters] = useState<TimesheetReportFilters>({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
  })
  const [shouldFetch, setShouldFetch] = useState(false)
  const [selectedDay, setSelectedDay] = useState<SelectedDay | null>(null)
  // Фильтр по должности — клиентский, поверх уже загруженного табеля
  const [positionId, setPositionId] = useState<string | undefined>(undefined)

  const { data: organizations } = useOrganizationsForSelect()
  const { data: departments } = useDepartmentsForSelect(filters.organizationId || null)

  const {
    data: report,
    isLoading,
    error,
    isFetching,
  } = useTimesheetReport(filters, shouldFetch)

  // Список должностей строим из сотрудников сформированного отчёта
  const positions = useMemo(() => {
    if (!report) return []
    const unique = new Map<string, string>()
    for (const emp of report.employees) {
      if (emp.position_id && emp.position_name) {
        unique.set(emp.position_id, emp.position_name)
      }
    }
    return Array.from(unique, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'ru'),
    )
  }, [report])

  // Если выбранной должности нет в новом отчёте — сбрасываем фильтр
  useEffect(() => {
    if (positionId && !positions.some((p) => p.id === positionId)) {
      setPositionId(undefined)
    }
  }, [positions, positionId])

  const filteredReport = useMemo(() => {
    if (!report || !positionId) return report
    const employees = report.employees.filter((e) => e.position_id === positionId)
    return {
      ...report,
      employees,
      meta: { ...report.meta, total_employees: employees.length },
    }
  }, [report, positionId])

  const handleFiltersChange = (newFilters: Partial<TimesheetReportFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  const handleGenerateReport = () => {
    setShouldFetch(true)
  }

  const handleDayClick = (selection: SelectedDay) => {
    setSelectedDay(selection)
  }

  const handleExport = useCallback(async () => {
    if (filteredReport && filteredReport.employees.length > 0) {
      try {
        await exportTimesheetToExcel(filteredReport)
      } catch (error) {
        console.error('Export failed:', error)
      }
    }
  }, [filteredReport])

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <TimesheetFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onGenerateReport={handleGenerateReport}
            onExport={handleExport}
            canExport={!!(filteredReport && filteredReport.employees.length > 0)}
            organizations={organizations || []}
            departments={departments || []}
            positions={positions}
            positionId={positionId}
            onPositionChange={setPositionId}
            isLoading={isLoading || isFetching}
          />
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки табеля. Попробуйте еще раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {shouldFetch && (
        <Card className="flex flex-col" style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Табель учёта рабочего времени
            </CardTitle>
            <CardDescription>
              {filteredReport ? (
                <>
                  {filteredReport.meta.month_name} {filteredReport.meta.year} | Сотрудников: {filteredReport.meta.total_employees} | Дней в месяце: {filteredReport.meta.days_in_month}
                </>
              ) : (
                'Загрузка...'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col overflow-hidden pb-0">
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : filteredReport && filteredReport.employees.length > 0 ? (
              <TimesheetGrid
                employees={filteredReport.employees}
                meta={filteredReport.meta}
                onDayClick={handleDayClick}
              />
            ) : (
              <div className="text-center py-8">
                <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  Нет данных
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {positionId
                    ? 'Нет сотрудников с выбранной должностью в этом табеле.'
                    : 'Нет сотрудников для отображения в табеле за выбранный период.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial state - before report generation */}
      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Выберите период и нажмите "Сформировать табель"
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Табель покажет фактические входы/выходы всех сотрудников за выбранный месяц.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Day details modal */}
      <TimesheetDayDetailsModal
        open={!!selectedDay}
        onOpenChange={(open) => !open && setSelectedDay(null)}
        employee={selectedDay?.employee || null}
        dateStr={selectedDay?.dateStr || null}
        day={selectedDay?.day || null}
      />
    </div>
  )
}
